// frontend/src/components/LitterManagement.js
// REFACTORED version with row-selection, improved modal layout, and smarter genotype editing.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { DataService } from '../services/DataService';
import { validateUniqueTags } from '../utils/validators';
import { ConfirmDialog } from './ConfirmDialog';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import { SkeletonRow } from './SkeletonRow';
import { useGeneticsOptions } from '../hooks/useGeneticsOptions';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';
import LitterForm from './litters/LitterForm';
import WeaningConfirm from './litters/WeaningConfirm';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];


const userColors = ['#D75B5B', '#6B8E23', '#F2A65B', '#8E7C4F', '#1F7A3D', '#1E3A3A', '#5F4B8B'];

const getUserColor = (userId) => {
  if (!userId) return '#CCCCCC'; // Default color for unknown user
  const index = userId % userColors.length;
  return userColors[index];
};

const LitterManagement = ({ activeView }) => {
    const { user, token, currentViewMode } = useAuth();
    const toast = useToast();
    const [litters, setLitters] = useState([]);
    const [mice, setMice] = useState([]);
    const [cages, setCages] = useState([]);
    const [strains, setStrains] = useState([]);
    const [labMembers, setLabMembers] = useState([]);

    const [loading, setLoading] = useState(false);
    // Phase 6: action outcomes are toasts. Two inline messages survive, for the
    // two reasons a toast is the wrong shape: `loadError` explains a page that
    // is otherwise blank, and `formError` belongs beside the fields it is about
    // and must persist while the user fixes them.
    const [loadError, setLoadError] = useState('');
    const [formError, setFormError] = useState('');
    const [confirmDialog, setConfirmDialog] = useState(null);
    const showConfirm = useCallback((message, options = {}) => new Promise(resolve => {
        setConfirmDialog({ message, resolve, ...options });
    }), []);
    const isFetchingRef = useRef(false);
    const fetchLitterDataRef = useRef(null);
    const fetchRelatedDataRef = useRef(null);
    const lastTapRef = useRef({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTransferringLitters, setIsTransferringLitters] = useState(false);

    // Mobile view state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // State for pup management modal
    const [showPupManagementModal, setShowPupManagementModal] = useState(false);
    const [managingLitter, setManagingLitter] = useState(null);
    const [pupsSelectedForManagement, setPupsSelectedForManagement] = useState([]);
    const [bulkEditState, setBulkEditState] = useState({ sex: '', genotype_data: [] });
    const [tagFormula, setTagFormula] = useState('cage_dob_#');
    const [isBulkEditExpanded, setIsBulkEditExpanded] = useState(false); // NEW: State for mobile bulk edit takeover


    // State for the weaning modal
    const [showWeanPupsModal, setShowWeanPupsModal] = useState(false);
    const [weaningLitter, setWeaningLitter, ] = useState(null);
    const [pupsSelectedForWeaning, setPupsSelectedForWeaning] = useState([]);
    const [pupCageAssignments, setPupCageAssignments] = useState({});
    const [bulkAssignCageId, setBulkAssignCageId] = useState('');
    const [autoGroupedPups, setAutoGroupedPups] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [selectedGroups, setSelectedGroups] = useState([]);

    const {
        locusOptions: tempLocusOptions,
        alleleOptions: tempAlleleOptions,
        fetchGenotypeOptions,
    } = useGeneticsOptions();

    // State for new cage shortcut in weaning modal
    const [showNewCageFormWeaning, setShowNewCageFormWeaning] = useState(false);
    const [newCageWeaning, setNewCageWeaning] = useState({ display_cage_number: '', location: '' });

    const [selectedLittersForActions, setSelectedLittersForActions] = useState([]);

   // State for transfers
   const [showTransferModal, setShowTransferModal] = useState(false);
   const [transferRecipientId, setTransferRecipientId] = useState('');

    // Pagination states for Litters table
    const [currentPageLitters, setCurrentPageLitters] = useState(1);
    const [itemsPerPageLitters, setItemsPerPageLitters] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
    const [totalLittersCount, setTotalLittersCount] = useState(0);

    // Sorting states for Litters table
    const [sortColumnLitters, setSortColumnLitters] = useState('date_of_birth');
    const [sortOrderLitters, setSortOrderLitters] = useState('asc');

    // Search states for Litters
    const [searchQueryLitters, setSearchQueryLitters] = useState('');
    const debouncedSearchLitters = useDebounce(searchQueryLitters, 350);

    const [transnetyxRefreshToken, setTransnetyxRefreshToken]=useState('');


    // Keep refs pointing at the latest closures.
    useEffect(() => { fetchLitterDataRef.current = fetchLitterData; });
    useEffect(() => { fetchRelatedDataRef.current = fetchRelatedData; });

    // Re-fetch after any sync completes — silently (no skeleton flash).
    useEffect(() => {
        const onSync = () => {
            if (isFetchingRef.current) return;
            fetchLitterDataRef.current?.(true);
            fetchRelatedDataRef.current?.();
        };
        window.addEventListener('squeakbase-sync-complete', onSync);
        return () => window.removeEventListener('squeakbase-sync-complete', onSync);
    }, []);

    // Check for mobile view on resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Effect to pre-populate bulk genotype editor with inherited loci
    useEffect(() => {
        if (pupsSelectedForManagement.length > 0 && managingLitter) {
            const selectedPups = managingLitter.pup_records.filter(p => pupsSelectedForManagement.includes(p.id));
            const uniqueLoci = new Set();
            selectedPups.forEach(pup => {
                pup.genotype_data.forEach(g => {
                    if (g.locus_name) uniqueLoci.add(g.locus_name);
                });
            });

            // Only prefill loci that are still active options — a deactivated locus
            // would otherwise render a blank select in the bulk editor.
            const activeLocusNames = new Set(tempLocusOptions.map(l => l.locus_name));
            const inheritedGenotypeData = Array.from(uniqueLoci)
                .filter(locus => activeLocusNames.has(locus))
                .map(locus => ({
                    locus_name: locus,
                    allele_name: ''
                }));

            setBulkEditState({ sex: '', genotype_data: inheritedGenotypeData });
        } else {
            setBulkEditState({ sex: '', genotype_data: [] });
        }
    }, [pupsSelectedForManagement, managingLitter, tempLocusOptions]);


    // NEW: Effect to close bulk edit panel if no pups are selected
    useEffect(() => {
        if (pupsSelectedForManagement.length === 0) {
            setIsBulkEditExpanded(false);
        }
    }, [pupsSelectedForManagement]);

    useEffect(() => {
      if (isMobile && currentPageLitters > 1) {
        setIsLoadingMore(true);
        fetchLitterData(true).finally(() => setIsLoadingMore(false));
      } else {
        fetchLitterData();
        fetchRelatedData();
        fetchStrains();
        fetchLabMembers();
      }
    }, [currentPageLitters, itemsPerPageLitters, sortColumnLitters, sortOrderLitters, debouncedSearchLitters]);

    // Reset filters/search when user clicks the active Litters tab again
    useEffect(() => {
      const handleTabReset = (e) => {
        if (e.detail?.view === 'litters') {
          setSearchQueryLitters('');
          setCurrentPageLitters(1);
        }
      };
      window.addEventListener('squeakbase-tab-reset', handleTabReset);
      return () => window.removeEventListener('squeakbase-tab-reset', handleTabReset);
    }, []);

    // On mobile, reset to page 1 when filters/search/sort change
    useEffect(() => {
      if (isMobile && currentPageLitters !== 1) {
        setCurrentPageLitters(1);
      }
    }, [isMobile, sortColumnLitters, sortOrderLitters, debouncedSearchLitters]);

    const handleApplyTagFormula = () => {
        if (!managingLitter || !tagFormula.trim()) return;
        
        // Extract variables for formula processing
        const cageNumber = managingLitter.display_cage_number || 'Unknown';
        const dobFormatted = managingLitter.date_of_birth 
            ? managingLitter.date_of_birth.replace(/-/g, '') // Convert YYYY-MM-DD to YYYYMMDD
            : 'Unknown';
        const litterNumber = managingLitter.litter_number || 'Unknown';
        
        let nonWeanedCounter = 0;
        const updatedPupRecords = managingLitter.pup_records.map((pup) => {
            if (pup.weaned) return pup; // Don't change weaned pups

            nonWeanedCounter++;
            let processedTag = tagFormula;

            // Replace variables in formula
            processedTag = processedTag.replace(/cage_number|cage/g, cageNumber);
            processedTag = processedTag.replace(/dob/g, dobFormatted);
            processedTag = processedTag.replace(/litter_number|litter/g, litterNumber);
            processedTag = processedTag.replace(/#/g, nonWeanedCounter.toString());

            return { ...pup, physical_tag: processedTag };
        });
        
        setManagingLitter({ ...managingLitter, pup_records: updatedPupRecords });
        toast.success(`Tag formula applied to ${updatedPupRecords.filter(p => !p.weaned).length} pup(s)!`);
    };

   const fetchLabMembers = async () => {
       if (!token) return;
       try {
           const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/labs/members`);
           setLabMembers(response.data.members.filter(member => member.id !== user.id)); // Exclude current user
       } catch (err) {
           console.error('Failed to fetch lab members:', err);
       }
   };

   const handleTransferSelectedLitters = () => {
       if (selectedLittersForActions.length === 0) {
           toast.warn('Please select at least one litter to transfer.');
           return;
       }
       setFormError('');
       setTransferRecipientId('');
       setShowTransferModal(true);
   };

   const handleConfirmLitterTransfer = async () => {
       if (isTransferringLitters) return;
       if (!transferRecipientId) {
           setFormError('Please select a recipient.');
           return;
       }
       setFormError('');

       setIsTransferringLitters(true);
       try {
           const payload = {
               litters_to_transfer: selectedLittersForActions,
               recipient_user_id: transferRecipientId
           };

           const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/litters/transfer`, payload);
           await DataService.sync();
           toast.success(response.data.message);
           setShowTransferModal(false);
           setTransferRecipientId('');
           setSelectedLittersForActions([]);
           fetchLitterData();
       } catch (err) {
           toast.error(err.response?.data?.error || 'Failed to transfer litters.');
           console.error('Litter transfer error:', err);
       } finally {
           setIsTransferringLitters(false);
       }
   };

    const fetchStrains = async () => {
        try {
            // Try local DB first (populated by sync)
            const localStrains = await DataService.getStrains();
            if (localStrains.length > 0) {
                setStrains(localStrains);
                return;
            }
            // Fallback to API
            if (!token || !navigator.onLine) return;
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/strains?_limit=${MAX_LIST_LIMIT}`);
            setStrains(response.data.data);
        } catch (err) {
            console.error('Failed to fetch strains:', err);
        }
    };

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (showPupManagementModal) {
                    setShowPupManagementModal(false);
                    setManagingLitter(null);
                } else if (showWeanPupsModal) {
                    setShowWeanPupsModal(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [showWeanPupsModal, showPupManagementModal]);

    // Lock body scroll when any modal is open to prevent background scroll-through on iOS
    const scrollYRef = useRef(0);
    const modalOverlayRef = useCallback((overlay) => {
        if (overlay) {
            // iOS WKWebView needs touchmove prevention on the overlay itself
            const handleTouchMove = (e) => {
                // Allow scrolling inside elements that are actually scrollable
                let target = e.target;
                while (target && target !== overlay) {
                    if (target.scrollHeight > target.clientHeight &&
                        window.getComputedStyle(target).overflowY !== 'hidden' &&
                        window.getComputedStyle(target).overflowY !== 'visible') {
                        return; // Let the scroll happen inside this scrollable element
                    }
                    target = target.parentElement;
                }
                e.preventDefault(); // Block scroll-through on non-scrollable areas
            };
            overlay.addEventListener('touchmove', handleTouchMove, { passive: false });
        }
    }, []);

    useEffect(() => {
        const modalOpen = showPupManagementModal || showWeanPupsModal;
        if (modalOpen) {
            scrollYRef.current = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollYRef.current}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollYRef.current);
            };
        }
    }, [showPupManagementModal, showWeanPupsModal]);

    const handleDoubleClick = (litter) => {
        handleEditLitter(litter);
    };

    const fetchLitterData = async (silent = false) => {
        isFetchingRef.current = true;
        if (!silent) setLoading(true);
        // Silent re-fetches (sync, tab switch) need all accumulated pages to avoid duplicates
        const needsFullRefresh = silent && isMobile && currentPageLitters > 1;
        const fetchPage = needsFullRefresh ? 1 : currentPageLitters;
        const fetchLimit = needsFullRefresh ? currentPageLitters * itemsPerPageLitters : itemsPerPageLitters;
        try {
            if (!navigator.onLine) {
                // Offline: read from local cache
                const result = await DataService.getLitters({
                    status: 'Active',
                    search: debouncedSearchLitters,
                    sortColumn: sortColumnLitters,
                    sortOrder: sortOrderLitters,
                    page: fetchPage,
                    limit: fetchLimit,
                });
                if (isMobile && currentPageLitters > 1 && !needsFullRefresh) {
                    setLitters(prev => [...prev, ...result.data]);
                } else {
                    setLitters(result.data);
                }
                setTotalLittersCount(result.totalCount);
                setLoadError('');
                return;
            }

            // The search query is correctly passed as is
            const litterParams = new URLSearchParams({
                _page: fetchPage,
                _limit: fetchLimit,
                _sort: sortColumnLitters,
                _order: sortOrderLitters,
                filter_status: 'Active',
                ...(debouncedSearchLitters && { search: debouncedSearchLitters }),
            }).toString();

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/litters?${litterParams}`);
            if (isMobile && currentPageLitters > 1 && !needsFullRefresh) {
                setLitters(prev => [...prev, ...response.data.data]);
            } else {
                setLitters(response.data.data);
            }
            setTotalLittersCount(response.data.totalCount);
            setLoadError('');
        } catch (err) {
            setLoadError('Failed to load litters');
            console.error('Fetch litters error:', err);
        } finally {
            isFetchingRef.current = false;
            setLoading(false);
        }
    };

    const fetchRelatedData = async () => {
        try {
            // Try cache first for mice and cages
            const [cachedMice, cachedCages] = await Promise.all([
                DataService.getMice({ status: 'Active', limit: 1000000 }),
                DataService.getCages(),
            ]);
            if (cachedMice.data.length > 0) setMice(cachedMice.data);
            if (cachedCages.length > 0) setCages(cachedCages);
            if ((cachedMice.data.length > 0 && cachedCages.length > 0) || !navigator.onLine) return;

            const [miceRes, cagesRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/mice?_limit=${MAX_LIST_LIMIT}&filter_status=Active`),
                axios.get(`${process.env.REACT_APP_API_URL}/api/cages?_limit=${MAX_LIST_LIMIT}&filter_status=Active`),
            ]);
            setCages(cagesRes.data.data);
            setMice(miceRes.data.data);
        } catch (err) {
            console.error('Failed to fetch related data (mice, cages):', err);
        }
    };

    const getUniqueParentLoci = (motherGenotype, fatherGenotype) => {
        const uniqueLoci = new Set();
        const allParentGenotypes = [...(motherGenotype || []), ...(fatherGenotype || [])];
        allParentGenotypes.forEach(g => g.locus_name && uniqueLoci.add(g.locus_name));

        // Helper to sort alleles: alphanumeric, but '+' always goes last
        const alleleSort = (a, b) => {
            if (a === b) return 0;
            if (a === '+') return 1;
            if (b === '+') return -1;
            return a.localeCompare(b);
        };

        // Helper to parse allele string
        const parseAllele = (alleleStr) => {
            if (!alleleStr) return ['+', '+']; // Treat missing/empty as Wild Type
            if (!alleleStr.includes('/')) return null; // Unsafe format (e.g. BAC)
            const parts = alleleStr.split('/').map(p => p.trim());
            if (parts.length !== 2) return null; // Must have exactly 2 parts
            return parts;
        };

        return Array.from(uniqueLoci).map(locusName => {
            // Find alleles for this locus in parents
            const motherAlleleObj = (motherGenotype || []).find(g => g.locus_name === locusName);
            const fatherAlleleObj = (fatherGenotype || []).find(g => g.locus_name === locusName);

            const motherAlleleStr = motherAlleleObj ? motherAlleleObj.allele_name : null;
            const fatherAlleleStr = fatherAlleleObj ? fatherAlleleObj.allele_name : null;

            const gametes1 = parseAllele(motherAlleleStr);
            const gametes2 = parseAllele(fatherAlleleStr);

            let inheritedAllele = '';

            if (gametes1 && gametes2) {
                const outcomes = [];
                // Punnett Square: 2x2 = 4 outcomes
                for (const g1 of gametes1) {
                    for (const g2 of gametes2) {
                        // Sort parts to ensure consistency (e.g. "A/+" instead of "+/A")
                        const pair = [g1, g2].sort(alleleSort);
                        outcomes.push(pair.join('/'));
                    }
                }

                // Check if all outcomes are identical (Guaranteed Genotype)
                if (outcomes.length > 0 && outcomes.every(o => o === outcomes[0])) {
                    inheritedAllele = outcomes[0];
                }
            }

            return {
                locus_name: locusName,
                allele_name: inheritedAllele
            };
        });
    };

    const getStrainNameById = (strainId) => {
        if (!strainId) return 'N/A';
        const strain = strains.find(s => s.id === strainId);
        return strain ? strain.strain_name : 'Unknown Strain';
    };

    const resetForm = () => {
        setShowPupManagementModal(false);
        setManagingLitter(null);
        setFormError('');
        setSelectedLittersForActions([]);
        setTagFormula('cage_dob_#');
        // Reset weaning-related state
        setShowNewCageFormWeaning(false);
        setNewCageWeaning({ display_cage_number: '', location: '' });
        setWeaningLitter(null);
        setPupsSelectedForWeaning([]);
        setPupCageAssignments({});
        setBulkAssignCageId('');
        // Reset pup management state
        setPupsSelectedForManagement([]);
        setBulkEditState({ sex: '', genotype_data: [] });
    };

    const handleEditLitter = (litter) => {
        resetForm();
        // Backend now ensures pup_records are created with the litter, so no need for frontend initialization.
        // Defensively parse in case old cached data stored pup_records as a JSON string.
        const rawPupRecords = litter.pup_records || [];
        const pupRecords = typeof rawPupRecords === 'string' ? (() => { try { return JSON.parse(rawPupRecords); } catch { return []; } })() : rawPupRecords;

        setManagingLitter({
            ...litter,
            date_of_birth: litter.date_of_birth ? litter.date_of_birth.split('T')[0] : '',
            pup_records: pupRecords,
            notes: litter.notes || ''
        });
        setShowPupManagementModal(true);
        setFormError('');
        fetchGenotypeOptions(true);
    };

    const handleSavePupRecords = async () => {
        if (!managingLitter || isSubmitting) return;

        // C14: duplicate tags within a litter make the pups indistinguishable in
        // every downstream view, and nothing caught it before — the collision
        // only became visible once someone tried to tell two mice apart.
        const tagError = validateUniqueTags(managingLitter.pup_records);
        if (tagError) {
            setFormError(tagError);
            return;
        }
        setFormError('');

        setIsSubmitting(true);
        try {
            // Pass the full managingLitter so cage_id, mother_id, genotypes etc. are never lost.
            // DataService strips joined/computed fields before sending to the server.
            const updated = { ...managingLitter, initial_pup_count: managingLitter.pup_records.length };
            setLitters(prev => prev.map(l => l.id === updated.id ? { ...l, ...updated } : l));
            await DataService.saveLitter(updated);
            toast.success('Litter changes saved successfully!');
            setShowPupManagementModal(false);
            setManagingLitter(null);
            fetchLitterData();
        } catch (err) {
            toast.error(err.message || 'Failed to save pup records');
            console.error('Save pup records error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePupChange = (pupIndex, field, value) => {
        if (!managingLitter) return;
        const updatedPupRecords = [...managingLitter.pup_records];
        updatedPupRecords[pupIndex] = { ...updatedPupRecords[pupIndex], [field]: value };
        setManagingLitter({ ...managingLitter, pup_records: updatedPupRecords });
    };

    const handleLitterDetailChange = (field, value) => {
        if (!managingLitter) return;
        setManagingLitter({ ...managingLitter, [field]: value });
    };

    const handlePupManagementCheckboxChange = (pupId) => {
        setPupsSelectedForManagement(prev => {
            return prev.includes(pupId) ? prev.filter(id => id !== pupId) : [...prev, pupId];
        });
    };
    
    const handleBulkGenotypeChange = (index, field, value) => {
        const updatedGenotype = [...bulkEditState.genotype_data];
        updatedGenotype[index] = { ...updatedGenotype[index], [field]: value };
        if (field === 'locus_name') {
            updatedGenotype[index].allele_name = ''; // Reset allele when locus changes
        }
        setBulkEditState({ ...bulkEditState, genotype_data: updatedGenotype });
    };

    const addBulkGenotypeLocus = () => {
        setBulkEditState({ ...bulkEditState, genotype_data: [...bulkEditState.genotype_data, { locus_name: '', allele_name: '' }] });
    };

    const removeBulkGenotypeLocus = (index) => {
        const filteredGenotype = bulkEditState.genotype_data.filter((_, i) => i !== index);
        setBulkEditState({ ...bulkEditState, genotype_data: filteredGenotype });
    };

    const handleApplyBulkPupChanges = () => {
        if (pupsSelectedForManagement.length === 0) return;

        const assignedSex = bulkEditState.sex;
        let oppositeSex = '';
        let oppositeSexAssignedCount = 0; // To track how many were auto-assigned.

        if (assignedSex === 'Male') {
            oppositeSex = 'Female';
        } else if (assignedSex === 'Female') {
            oppositeSex = 'Male';
        }

        const updatedPupRecords = managingLitter.pup_records.map(pup => {
            const isSelected = pupsSelectedForManagement.includes(pup.id);

            if (isSelected) {
                const updatedPup = { ...pup };
                if (bulkEditState.sex) {
                    updatedPup.sex = bulkEditState.sex;
                }
                // Only update genotype if there's at least one valid locus defined
                const validGenotypeEntries = bulkEditState.genotype_data.filter(g => g.locus_name && g.allele_name);
                if (validGenotypeEntries.length > 0) {
                    // Start with a copy of the pup's existing genotype data
                    let mergedGenotypeData = [...(pup.genotype_data || [])]; 

                    validGenotypeEntries.forEach(newGenotype => {
                        const existingIndex = mergedGenotypeData.findIndex(
                            existingGenotype => existingGenotype.locus_name === newGenotype.locus_name
                        );

                        if (existingIndex !== -1) {
                            // Locus exists, update the allele
                            mergedGenotypeData[existingIndex] = {
                                ...mergedGenotypeData[existingIndex],
                                allele_name: newGenotype.allele_name
                            };
                        } else {
                            // New locus, append it
                            mergedGenotypeData.push({
                                locus_name: newGenotype.locus_name,
                                allele_name: newGenotype.allele_name
                            });
                        }
                    });

                    updatedPup.genotype_data = mergedGenotypeData;                }
                return updatedPup;
            }
            else if (oppositeSex && pup.sex === 'Unassigned') {
                oppositeSexAssignedCount++;
                return { ...pup, sex: oppositeSex };
            }
            return pup;
        });

        let successMessage = `${pupsSelectedForManagement.length} pup(s) updated.`;
        if (oppositeSexAssignedCount > 0) {
            successMessage += ` ${oppositeSexAssignedCount} remaining pup(s) assigned as ${oppositeSex}.`;
        }
        successMessage += ' Save changes to confirm.';

        setManagingLitter({ ...managingLitter, pup_records: updatedPupRecords });
        setPupsSelectedForManagement([]);
        toast.success(successMessage);
    };

    const handleAddPup = () => {
        if (!managingLitter) return;
        // Logic to create a new pup record
        const newPup = {
            id: `temp-${Date.now()}`, // Temporary unique ID
            physical_tag: '',
            sex: 'Unassigned',
            genotype_data: getUniqueParentLoci(managingLitter.mother_genotype, managingLitter.father_genotype),
            weaned: false,
            strain_id: managingLitter.strain_id,
        };
        const updatedPupRecords = [...managingLitter.pup_records, newPup];
        setManagingLitter({ ...managingLitter, pup_records: updatedPupRecords });
    };

    const handleRemovePup = async (pupIndex) => {
        const updatedPupRecords = managingLitter.pup_records.filter((_, idx) => idx !== pupIndex);
        if (updatedPupRecords.length === 0) {
            setShowPupManagementModal(false);
            await handleDeleteLitter(managingLitter.id, managingLitter.litter_number);
        } else {
            setManagingLitter({ ...managingLitter, pup_records: updatedPupRecords });
        }        
    };

    const handleTogglePupsTagged = async (litterId, currentStatus) => {
        // Capture the full litter data before the optimistic UI update
        const fullLitter = litters.find(l => l.id === litterId);
        if (!fullLitter) return;

        const originalLitters = [...litters];
        setLitters(prevLitters =>
            prevLitters.map(l =>
                l.id === litterId ? { ...l, all_pups_tagged: !currentStatus } : l
            )
        );

        try {
            await DataService.saveLitter({ ...fullLitter, all_pups_tagged: !currentStatus });
            toast.success('Tagged status updated.');
        } catch (err) {
            toast.error(err.message || 'Failed to update tagged status');
            setLitters(originalLitters);
            console.error('Update tagged status error:', err);
        }
    };
 
    // Helper function to calculate effective cage capacity
    const getEffectiveCageOccupancy = (cage) => {
        const assignedCount = Object.values(pupCageAssignments).filter(cageId => String(cageId) === String(cage.id)).length;
        // Prefer mice_in_cage.length (always an integer array) over mouse_count which can
        // be a bigint string from the pg driver or a concatenated string from offline edits.
        const baseCount = Array.isArray(cage.mice_in_cage) ? cage.mice_in_cage.length : (Number(cage.mouse_count) || 0);
        return baseCount + assignedCount;
    };

    // Helper function to group pups logically for weaning
    const createAutoGroupings = (pups) => {
        // First, separate by sex
        const males = pups.filter(p => p.sex === 'Male');
        const females = pups.filter(p => p.sex === 'Female');
        const unassigned = pups.filter(p => p.sex === 'Unassigned');
        
        const groups = [];
        const groupPupsBySex = (sexGroup, sexLabel) => {
            if (sexGroup.length > 0) {
                const totalPups = sexGroup.length;
                // Split into multiple groups if necessary to keep size <= 5
                const groupSize = 5;
                for (let i = 0; i < totalPups; i += groupSize) {
                    const chunk = sexGroup.slice(i, i + groupSize);
                    let label = `${sexLabel} - Group ${Math.floor(i / groupSize) + 1} (${chunk.length})`;
                    if (sexGroup.length <= 5) { // If all pups fit in one group, make the label simpler
                         label = `${sexLabel} (${chunk.length})`;
                    }
                    groups.push({
                        id: `${sexLabel.toLowerCase()}_group_${i}_${Date.now()}`,
                        label: label,
                        pups: chunk,
                        sex: sexLabel,
                        genotype: null // No longer grouping by genotype
       
                    });
                }
            }
        };
        groupPupsBySex(males, 'Male');
        groupPupsBySex(females, 'Female');
        groupPupsBySex(unassigned, 'Unassigned');
         
        
        return groups;
    };

    // Handle group selection
    const handleGroupCheckboxChange = (groupId) => {
        const group = autoGroupedPups.find(g => g.id === groupId);
        if (!group) return;

        const isCurrentlySelected = selectedGroups.includes(groupId);
        
        if (isCurrentlySelected) {
            // Deselecting group - remove group and all its pups from selections
            setSelectedGroups(prev => prev.filter(id => id !== groupId));
            setPupsSelectedForWeaning(prev => 
                prev.filter(pupId => !group.pups.some(pup => pup.id === pupId))
            );
        } else {
            // Selecting group - add group and all its pups to selections
            setSelectedGroups(prev => [...prev, groupId]);
            setPupsSelectedForWeaning(prev => {
                const newSelections = [...prev];
                group.pups.forEach(pup => {
                    if (!newSelections.includes(pup.id)) {
                        newSelections.push(pup.id);
                    }
                });
                return newSelections;
            });
        }
    };

    // Toggle group expansion
    const toggleGroupExpansion = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    // Apply bulk cage assignment to selected groups
    const handleApplyBulkGroupAssignment = () => {
        if (!bulkAssignCageId) {
            setFormError('Please select a cage to assign.');
            return;
        }
        if (selectedGroups.length === 0) {
            setFormError('Please select at least one group to assign.');
            return;
        }
        setFormError('');

        const newAssignments = { ...pupCageAssignments };
        let totalAssigned = 0;

        selectedGroups.forEach(groupId => {
            const group = autoGroupedPups.find(g => g.id === groupId);
            if (group) {
                group.pups.forEach(pup => {
                    newAssignments[pup.id] = bulkAssignCageId;
                    totalAssigned++;
                });
            }
        });

        setPupCageAssignments(newAssignments);
        setSelectedGroups([]);
        setPupsSelectedForWeaning([]);
        toast.success(`${totalAssigned} pup(s) in ${selectedGroups.length} group(s) assigned to cage. Click "Confirm Wean" to save.`);
    };

    const handleDeleteLitter = async (litterId, litterNumber) => {
        const isConfirmed = await showConfirm(
            `Are you sure you want to archive litter ${litterNumber}? This will unlink all weaned mice from this litter. This action cannot be undone by you.`,
            { confirmLabel: 'Yes, Archive', danger: true }
        );

        if (!isConfirmed) return;

        try {
            setLitters(prev => prev.filter(l => l.id !== litterId));
            await DataService.archiveLitter(litterId);
            toast.success(`Litter ${litterNumber} archived successfully!`);
        } catch (err) {
            fetchLitterData(); // rollback on error
            toast.error(err.response?.data?.error || 'Failed to archive litter.');
            console.error('Archive litter error:', err);
        }
    };

    const handleWeanPupsClick = (litter) => {
        const pupRecords = litter.pup_records || [];
        const unweanedPups = pupRecords.filter(pup => !pup.weaned);
        setWeaningLitter({ ...litter, pup_records: unweanedPups });
        setPupsSelectedForWeaning([]);
        setPupCageAssignments({});
        setBulkAssignCageId('');
        const groups = createAutoGroupings(unweanedPups);
        setAutoGroupedPups(groups);
        // Set all groups to expanded by default
        const expandedState = {};
        groups.forEach(group => {
            expandedState[group.id] = true;
        });
        setExpandedGroups(expandedState);
        setShowWeanPupsModal(true);
        setFormError('');
        fetchGenotypeOptions(true);
    };

    const handleWeanPupCheckboxChange = (pupId) => {
        setPupsSelectedForWeaning(prev => {
            const newSelections = prev.includes(pupId) 
                ? prev.filter(id => id !== pupId) 
                : [...prev, pupId];
            
            // Update group selections based on individual pup selections
            const newGroupSelections = [];
            autoGroupedPups.forEach(group => {
                const allPupsInGroupSelected = group.pups.every(pup => 
                    newSelections.includes(pup.id)
                );
                if (allPupsInGroupSelected) {
                    newGroupSelections.push(group.id);
                }
            });
            setSelectedGroups(newGroupSelections);
            
            return newSelections;
        });
    };

    const handleApplyBulkCageChange = () => {
        if (!bulkAssignCageId) {
            setFormError('Please select a cage to assign.');
            return;
        }
        if (pupsSelectedForWeaning.length === 0) {
            setFormError('Please select at least one pup to assign.');
            return;
        }
        setFormError('');
        const newAssignments = { ...pupCageAssignments };
        pupsSelectedForWeaning.forEach(pupId => { newAssignments[pupId] = bulkAssignCageId; });
        setPupCageAssignments(newAssignments);
        setPupsSelectedForWeaning([]);
        toast.success(`${pupsSelectedForWeaning.length} pup(s) assigned to cage. Click "Confirm Wean" to save.`);
    };

    const handleNewCageSubmitWeaning = async (e) => {
        e.preventDefault();
        try {
            let savedCage;

            if (!navigator.onLine) {
                // Offline: create temp cage via DataService so it queues for sync
                const tempId = `temp_${crypto.randomUUID()}`;
                savedCage = {
                    id: tempId,
                    display_cage_number: newCageWeaning.display_cage_number,
                    location: newCageWeaning.location,
                    status: 'Active',
                    capacity: 5,
                    mouse_count: 0,
                    breeding_status: 'Stock',
                    mice_in_cage: [],
                };
                await DataService.saveCage(savedCage);
            } else {
                // Online: POST directly to get a real server ID (avoids tempId/sync race)
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/cages`, {
                    display_cage_number: newCageWeaning.display_cage_number,
                    location: newCageWeaning.location,
                    capacity: 5,
                });
                savedCage = response.data;
                // Cache locally so weanLitter can resolve display_cage_number
                if (savedCage?.id) await DataService.putCageToCache(savedCage);
            }

            if (savedCage?.id) {
                // Directly add to state — don't call fetchRelatedData() which bails early
                setCages(prev => [...prev, savedCage]);
                setBulkAssignCageId(savedCage.id);
            }

            setShowNewCageFormWeaning(false);
            setNewCageWeaning({ display_cage_number: '', location: '' });
            toast.success('New cage created and available for selection!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to create new cage.');
            console.error('Create new cage error (weaning modal):', err);
        }
    };

    const handleConfirmWeanPups = async () => {
        if (!weaningLitter || isSubmitting) return;
        const pupsToWean = weaningLitter.pup_records.filter(pup => pupCageAssignments[pup.id]);
        if (pupsToWean.length === 0) {
            setFormError('No pups have been assigned to a cage.');
            return;
        }
        const unassignedSexPups = pupsToWean.filter(pup => !pup.sex || pup.sex === 'Unassigned');
        if (unassignedSexPups.length > 0) {
            setFormError(`${unassignedSexPups.length} pup(s) must have a sex assigned (Male or Female) before weaning.`);
            return;
        }
        setFormError('');
        setIsSubmitting(true);
        try {
            const weanedMiceData = pupsToWean.map(pup => ({
                pup_temp_id: pup.id,
                physical_tag: pup.physical_tag,
                sex: pup.sex,
                genotype_data: pup.genotype_data,
                cage_id: pupCageAssignments[pup.id],
                date_of_birth: weaningLitter.date_of_birth ? weaningLitter.date_of_birth.split('T')[0] : '',
                notes: weaningLitter.notes || '',
                strain_id: pup.strain_id,
            }));
            const weanedLitterId = weaningLitter.id;
            setLitters(prev => prev.filter(l => l.id !== weanedLitterId));
            await DataService.weanLitter(weanedLitterId, weanedMiceData);
            toast.success(`${pupsToWean.length} pup(s) weaned successfully!`);
            setShowWeanPupsModal(false);
            resetForm();
            fetchLitterData();
        } catch (err) {
            fetchLitterData(); // rollback on error
            toast.error(err.message || 'Failed to wean pups.');
            console.error('Wean pups error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLitterCardSelect = (litterId) => {
        setSelectedLittersForActions(prev => prev.includes(litterId) ? prev.filter(id => id !== litterId) : [...prev, litterId]);
    };

    const handleDeleteSelectedLitters = async () => {
        if (selectedLittersForActions.length === 0) {
            toast.warn('Please select at least one litter to archive.');
            return;
        }
        const isConfirmed = await showConfirm(
            `Are you sure you want to archive ${selectedLittersForActions.length} selected litter(s)? This will unlink all weaned mice from these litters. This action cannot be undone by you.`,
            { confirmLabel: 'Yes, Archive', danger: true }
        );

        if (!isConfirmed) return;

        try {
            const idsToRemove = [...selectedLittersForActions];
            setLitters(prev => prev.filter(l => !idsToRemove.includes(l.id)));
            await Promise.all(idsToRemove.map(id => DataService.archiveLitter(id)));
            toast.success(`${idsToRemove.length} litter(s) archived successfully!`);
            setSelectedLittersForActions([]);
        } catch (err) {
            fetchLitterData(); // rollback on error
            toast.error(err.response?.data?.error || 'Failed to archive selected litters.');
            console.error('Bulk archive error:', err);
        }
    };

    const handleWeanSelectedPups = () => {
        if (selectedLittersForActions.length !== 1) {
            toast.warn('Please select exactly one litter to wean pups from.');
            return;
        }
        const litterToWean = litters.find(litter => litter.id === selectedLittersForActions[0]);
        if (litterToWean) handleWeanPupsClick(litterToWean);
    };

    const formatGenotype = (genotypeArray) => {
        if (!genotypeArray || genotypeArray.length === 0) return 'WT';
        return genotypeArray.map(g => `${g.locus_name}(${g.allele_name || '?'})`).join(', ');
    };

    const formatDatabaseDateLiteral = (dateString) => {
        if (!dateString) return 'N/A';
        const dateObj = new Date(dateString);
        if (isNaN(dateObj.getTime())) return 'Invalid Date';
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        return `${month}/${day}/${year}`;
    };

    const calculateAgeInDays = (dobString) => {
        if (!dobString) return 'N/A';
        const dob = new Date(dobString);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const diffTime = Math.abs(now.getTime() - dob.getTime());
        return `${Math.floor(diffTime / (1000 * 60 * 60 * 24))} days`;
    };

    const handleSortLitters = (column) => {
        setSortOrderLitters(sortColumnLitters === column && sortOrderLitters === 'asc' ? 'desc' : 'asc');
        setSortColumnLitters(column);
        setCurrentPageLitters(1);
    };

    const handlePageChangeLitters = (pageNumber) => setCurrentPageLitters(pageNumber);
    const handleItemsPerPageChangeLitters = (e) => {
        setItemsPerPageLitters(parseInt(e.target.value, 10));
        setCurrentPageLitters(1);
    };
    const totalPagesLitters = Math.ceil(totalLittersCount / itemsPerPageLitters);

    const handleLoadMoreLitters = useCallback(() => {
      if (currentPageLitters < totalPagesLitters) {
        setCurrentPageLitters(prev => prev + 1);
      }
    }, [currentPageLitters, totalPagesLitters]);

    useInfiniteScroll({
      enabled: isMobile && activeView === 'litters',
      hasMore: currentPageLitters < totalPagesLitters,
      loading: loading || isLoadingMore,
      onLoadMore: handleLoadMoreLitters,
    });

    const handleCancelEdit = () => resetForm();

    const getPupSummary = (litter) => {
        try {
            if (litter.pup_records) {
                const pupRecords = litter.pup_records;
                const males = pupRecords.filter(p => p.sex === 'Male' && !p.weaned).length;
                const females = pupRecords.filter(p => p.sex === 'Female' && !p.weaned).length;
                const unassigned = pupRecords.filter(p => p.sex === 'Unassigned' && !p.weaned).length; // ADDED
                const weaned = pupRecords.filter(p => p.weaned).length;
                return { males, females, unassigned, weaned, total: pupRecords.length }; // UPDATED
            }
        } catch (e) { console.error('Error parsing pup records:', e); }
        return { males: 0, females: 0, unassigned: 0, weaned: litter.weaned_pup_count || 0, total: parseInt(litter.initial_pup_count) };
    };

    const handleSearchLitters = (e) => {
        setSearchQueryLitters(e.target.value);
        setCurrentPageLitters(1);
    };
    const clearSearchLitters = () => setSearchQueryLitters('');

    // RENDER FUNCTIONS
    // MODIFIED: This function now renders a full <details> element with its own state and actions.
    const renderMobileBulkManageControls = () => (
        <details className="bulk-assign-controls"
            style={{
                padding: '5px',
                backgroundColor: '#f8f9fa',
                borderRadius: '3px',
                border: '1px solid #e0e0e0',
                margin: '5px 0'
            }}
            open={isBulkEditExpanded}
            onToggle={(e) => setIsBulkEditExpanded(e.target.open)}
        >
            <summary style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                Bulk Edit ({pupsSelectedForManagement.length} selected)
            </summary>
            <div style={{ marginTop: '3px' }}>
                <div style={{ marginBottom: '3px' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '0.9em' }}>Assign Sex</label>
                    <select
                        value={bulkEditState.sex}
                        onChange={(e) => setBulkEditState({ ...bulkEditState, sex: e.target.value })}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9em' }}
                    >
                        <option value="">Don't Change</option>
                        <option value="Unassigned">Unassigned</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '0.9em' }}>Replace Genotype</label>
                    {bulkEditState.genotype_data.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                            <select value={entry.locus_name} onChange={(e) => handleBulkGenotypeChange(index, 'locus_name', e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9em' }}>
                                <option value="">Locus</option>
                                {tempLocusOptions.map(l => <option key={l.id} value={l.locus_name}>{l.locus_name}</option>)}
                            </select>
                            <select value={entry.allele_name} onChange={(e) => handleBulkGenotypeChange(index, 'allele_name', e.target.value)} disabled={!entry.locus_name} style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9em' }}>
                                <option value="">Allele</option>
                                {tempAlleleOptions.map(a => <option key={a.id} value={a.allele_name}>{a.allele_name}</option>)}
                            </select>
                        </div>
                    ))}
                </div>
                {/* DEDICATED buttons for this takeover view */}
                <div className="modal-actions" style={{ flexShrink: 0, marginTop: '3px', paddingTop: '3px', borderTop: '1px solid #eee', display: 'flex', gap: '10px' }}>
                     <button
                        type="button"
                        onClick={() => {
                            handleApplyBulkPupChanges();
                            setIsBulkEditExpanded(false);
                        }}
                        className="primary-button"
                        style={{flex: 1, fontSize: '0.9em'}}
                    >
                        Apply
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsBulkEditExpanded(false)}
                        className="secondary-button"
                        style={{flex: 1, fontSize: '0.9em'}}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </details>
    );

    const renderMobileAutoTagControls = () => (
        <details className="tag-formula-controls" style={{
            padding: '5px',
            backgroundColor: '#f8f9fa',
            borderRadius: '3px',
            border: '1px solid #e0e0e0',
            margin: '5px 0'
        }}>
            <summary style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                Auto-Tag All Pups
            </summary>
            <div style={{ marginTop: '3px' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '3px', fontSize: '0.7em' }}>Tag by Formula</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="text"
                        value={tagFormula}
                        onChange={(e) => setTagFormula(e.target.value)}
                        placeholder="cage_dob_#"
                        style={{
                            flex: '1',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '0.7em'
                        }}
                    />
                    <button
                        type="button"
                        onClick={handleApplyTagFormula}
                        className="primary-button"
                        style={{ padding: '8px 12px', fontSize: '0.7em', whiteSpace: 'nowrap' }}
                        disabled={!tagFormula.trim()}
                    >
                        Apply
                    </button>
                </div>
                <div style={{ fontSize: '0.7em', color: '#666', marginTop: '5px' }}>
                    Use <code>cage</code>, <code>dob</code>, <code>litter</code>, or <code>#</code>.
                </div>
            </div>
        </details>
    );

    const renderDesktopLitterTable = () => (
        <table className="litter-table">
            <thead>
                <tr>
                    <th style={{width: '30px'}}><input type="checkbox" onChange={(e) => setSelectedLittersForActions(e.target.checked ? litters.map(l => l.id) : [])} checked={selectedLittersForActions.length === litters.length && litters.length > 0} ref={input => { if (input) input.indeterminate = selectedLittersForActions.length > 0 && selectedLittersForActions.length < litters.length; }} /></th>
                    <th onClick={() => handleSortLitters('display_cage_number')}>Cage {sortColumnLitters === 'display_cage_number' ? (sortOrderLitters === 'asc' ? '▲' : '▼') : ''}</th>
                    <th onClick={() => handleSortLitters('date_of_birth')}>Age</th>
                    <th onClick={() => handleSortLitters('date_of_birth')}>DOB {sortColumnLitters === 'date_of_birth' ? (sortOrderLitters === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>Mother (Genotype)</th>
                    <th>Father (Genotype)</th>
                    <th>Pup Info</th>
                    <th>All Tagged</th>
                    <th onClick={() => handleSortLitters('notes')}>Notes {sortColumnLitters === 'notes' ? (sortOrderLitters === 'asc' ? '▲' : '▼') : ''}</th>
                    <th>Actions</th>
                    <th>Owner</th>
                </tr>
            </thead>
            <tbody>
                {litters.map(litter => {
                    const pupSummary = getPupSummary(litter);
                    return (
                        <tr key={litter.id} onClick={() => handleLitterCardSelect(litter.id)} onDoubleClick={() => handleDoubleClick(litter)} style={{cursor: 'pointer', backgroundColor: selectedLittersForActions.includes(litter.id) ? 'rgba(0,123,255,0.1)' : 'transparent'}}>                        
                            <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedLittersForActions.includes(litter.id)} onChange={() => handleLitterCardSelect(litter.id)} /></td>
                            <td>{litter.display_cage_number || 'N/A'}</td>
                            <td>{calculateAgeInDays(litter.date_of_birth)}</td>
                            <td>{formatDatabaseDateLiteral(litter.date_of_birth)}</td>
                            <td>{litter.mother_genotype && <p style={{marginTop: '0px'}}>{formatGenotype(litter.mother_genotype)}</p>}</td>
                            <td>{litter.father_genotype && <p style={{marginTop: '0px'}}>{formatGenotype(litter.father_genotype)}</p>}</td>
                            <td>{pupSummary.unassigned}U {pupSummary.males}M {pupSummary.females}F ({pupSummary.weaned} Weaned)</td>
                            <td onClick={(e) => e.stopPropagation()}>
                                <input 
                                    type="checkbox"
                                    checked={litter.all_pups_tagged || false} 
                                    onChange={() => handleTogglePupsTagged(litter.id, litter.all_pups_tagged)} 
                                />
                            </td>                            
                            <td style={{maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{litter.notes || ''}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                    <button onClick={() => handleEditLitter(litter)} className="action-btn primary" style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}>Manage</button>
                                </div>
                            </td>
                            <td style={{ justifyContent: 'center', alignItems: 'center' }}>
                                <div
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '8px',
                                        backgroundColor: getUserColor(litter.user_id),
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                    }}
                                    title={litter.owner_initials}
                                >{litter.owner_initials}</div>
                            </td>   
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    const handleDoubleTap = (id, item, callback) => {
        const now = Date.now();
        if (lastTapRef.current.id === id && now - lastTapRef.current.time < 300) {
            callback(item);
            lastTapRef.current = {};
        } else {
            lastTapRef.current = { id, time: now };
        }
    };

    const renderMobileLitterList = () => (
        <div className="mobile-list">
            {litters.map(litter => {
                const pupSummary = getPupSummary(litter);
                return (
                    <div key={litter.id} className="list-item" onClick={() => handleDoubleTap(litter.id, litter, handleEditLitter)} style={{cursor: 'pointer', backgroundColor: selectedLittersForActions.includes(litter.id) ? 'rgba(0,123,255,0.1)' : 'transparent'}}>
                        <div className="item-content">
                            <div className="item-title">Cage: {litter.display_cage_number || 'Unassigned litter'}</div>
                            <div className="item-subtitle">DOB: {formatDatabaseDateLiteral(litter.date_of_birth)} • Age: {calculateAgeInDays(litter.date_of_birth)}</div>
                            <div className="item-meta">
                                {pupSummary.males}M {pupSummary.females}F ({pupSummary.weaned} Weaned)
                                <span style={{ marginLeft: '10px' }} onClick={(e) => e.stopPropagation()}>
                                    <label>
                                        <input 
                                            type="checkbox" 
                                            checked={litter.all_pups_tagged || false} 
                                            onChange={() => handleTogglePupsTagged(litter.id, litter.all_pups_tagged)} 
                                            style={{ marginRight: '5px' }}
                                        />
                                        All Tagged
                                    </label>
                                </span>
                            </div>
                            {litter.notes && <div className="item-notes" style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>Notes: {litter.notes}</div>}
                            <div className="genotype-list" style={{ marginTop: '0.5rem' }}>
                                {litter.mother_genotype && <p className="genotype-chip">Mother: <strong>{formatGenotype(litter.mother_genotype)}</strong></p>}
                                {litter.father_genotype && <p className="genotype-chip">Father: <strong>{formatGenotype(litter.father_genotype)}</strong></p>}
                            </div>
                        </div>
                        <div className="item-actions column-actions" onClick={(e) => e.stopPropagation()}>
                            <button className="action-btn primary" onClick={() => handleEditLitter(litter)}>Manage</button>
                            <button className="action-btn" onClick={() => handleWeanPupsClick(litter)} style={{ backgroundColor: '#28a745', color: 'white' }}>Wean</button>
                            <button className="action-btn" onClick={() => handleDeleteLitter(litter.id, litter.litter_number)} style={{ backgroundColor: '#dc3545', color: 'white' }}>Archive</button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const renderPupManagementTable = () => (
        <table className="litter-table">
            <thead>
                <tr>
                    <th style={{ width: '40px' }}><input type="checkbox"
                        checked={pupsSelectedForManagement.length === managingLitter.pup_records.filter(p => !p.weaned).length && managingLitter.pup_records.filter(p => !p.weaned).length > 0}
                        onChange={(e) => setPupsSelectedForManagement(e.target.checked ? managingLitter.pup_records.filter(p => !p.weaned).map(p => p.id) : [])} />
                    </th>
                    <th>#</th>
                    <th>Physical Tag</th>
                    <th>Sex</th>
                    <th>Genotype</th>
                    <th>Strain</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {managingLitter.pup_records.map((pup, index) => (
                    <tr key={pup.id}
                        onClick={() => !pup.weaned && handlePupManagementCheckboxChange(pup.id)}
                        style={{
                            cursor: pup.weaned ? 'default' : 'pointer',
                            backgroundColor: pupsSelectedForManagement.includes(pup.id) ? '#e7f3ff' : (pup.weaned ? '#f0f8ff' : 'white')
                        }}>
                        <td onClick={(e) => e.stopPropagation()}>
                            {!pup.weaned && <input type="checkbox" checked={pupsSelectedForManagement.includes(pup.id)} onChange={() => handlePupManagementCheckboxChange(pup.id)} />}
                        </td>
                        <td>{index + 1}</td>
                        <td style={{ maxWidth: '120px' }} onClick={(e) => e.stopPropagation()}>
                            {pup.weaned ? (<span>{pup.physical_tag || 'N/A'}</span>) : (
                                <input type="text" value={pup.physical_tag} onChange={(e) => handlePupChange(index, 'physical_tag', e.target.value)} placeholder="e.g., Green-1" style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }} />
                            )}
                        </td>
                        <td>{pup.sex}</td>
                        <td>{formatGenotype(pup.genotype_data)}</td>
                        <td>{getStrainNameById(pup.strain_id)}</td>
                        <td><span style={{ color: pup.weaned ? '#28a745' : '#6c757d', fontWeight: 'bold' }}>{pup.weaned ? 'Weaned' : 'Not Weaned'}</span></td>
                        <td onClick={(e) => e.stopPropagation()} style={{ marginLeft: '10px' }}>
                            {!pup.weaned && (
                                <button type="button" onClick={() => handleRemovePup(index)} className="quarternary-cancel-button" title="Remove Pup" style={{ marginLeft: '10px' }}>X</button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderMobilePupManagement = () => (
        <div className="mobile-list">
            {managingLitter.pup_records.map((pup, index) => (
                <div key={pup.id} 
                    className="list-item" 
                    onClick={() => !pup.weaned && handlePupManagementCheckboxChange(pup.id)}
                    style={{ 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        cursor: pup.weaned ? 'default' : 'pointer',
                        backgroundColor: pupsSelectedForManagement.includes(pup.id) ? '#e7f3ff' : (pup.weaned ? '#f0f8ff' : 'white') 
                    }}>
                    {!pup.weaned && (
                        <div className="item-selection" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={pupsSelectedForManagement.includes(pup.id)} onChange={() => handlePupManagementCheckboxChange(pup.id)} style={{ marginRight: '10px' }}/>
                        </div>
                    )}
                    <div className="item-content" style={{ flex: 1 }}>
                        <div className="item-title" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            <span>Pup #{index + 1} - {pup.weaned ? 'Weaned' : pup.sex}</span>
                            <span className="item-meta">Genotype: {formatGenotype(pup.genotype_data)}</span>
                            <span className="item-meta">Strain: {getStrainNameById(pup.strain_id)}</span>
                        </div>
                        <div style={{ flex: 1, marginTop: '10px' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <label style={{ fontSize: '0.9em', fontWeight: 'bold' }}>Tag:</label>
                            {pup.weaned ? (<span>{pup.physical_tag || 'N/A'}</span>) : (
                                <input type="text" value={pup.physical_tag} onChange={(e) => handlePupChange(index, 'physical_tag', e.target.value)} placeholder="e.g., Green-1" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }} />
                            )}
                            </div>
                        </div>
                    </div>
                    {!pup.weaned && (
                        <div className="item-actions" style={{ marginLeft: '10px', alignSelf: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <button type="button" onClick={() => handleRemovePup(index)} className="quarternary-cancel-button" title="Remove Pup" style={{ padding: '0 5px' }}>X</button>
                         </div>
                    )}
                </div>
            ))}
        </div>
    );
    
    const renderBulkManageControls = () => {
        return (
            <div className="bulk-assign-controls" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0', minWidth: '350px', opacity: pupsSelectedForManagement.length === 0 ? 0.7 : 1 }}>
                <h4 style={{ marginTop: 0, marginBottom: '5px' }}>Bulk Edit Pups</h4>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: 0, marginBottom: '15px' }}>
                    {pupsSelectedForManagement.length > 0 ? `${pupsSelectedForManagement.length} pup(s) selected.` : 'Select pups from the list to enable editing.'}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Sex Assignment */}
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Assign Sex</label>
                        <select
                            value={bulkEditState.sex}
                            onChange={(e) => setBulkEditState({ ...bulkEditState, sex: e.target.value })}
                            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                            disabled={pupsSelectedForManagement.length === 0}
                        >
                            <option value="">Don't Change</option>
                            <option value="Unassigned">Unassigned</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>
    
                    {/* Genotype Assignment */}
                    <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Replace Genotype</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto', paddingRight: '10px' }}>
                            {bulkEditState.genotype_data.map((entry, index) => (
                                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <select
                                        value={entry.locus_name}
                                        onChange={(e) => handleBulkGenotypeChange(index, 'locus_name', e.target.value)}
                                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        disabled={pupsSelectedForManagement.length === 0}
                                    >
                                        <option value="">Select Locus</option>
                                        {tempLocusOptions.map(l => <option key={l.id} value={l.locus_name}>{l.locus_name}</option>)}
                                    </select>
                                    <select
                                        value={entry.allele_name}
                                        onChange={(e) => handleBulkGenotypeChange(index, 'allele_name', e.target.value)}
                                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        disabled={pupsSelectedForManagement.length === 0 || !entry.locus_name}
                                    >
                                        <option value="">Select Allele</option>
                                        {tempAlleleOptions.map(a => <option key={a.id} value={a.allele_name}>{a.allele_name}</option>)}
                                    </select>
                                    <button type="button" onClick={() => removeBulkGenotypeLocus(index)} className="quarternary-cancel-button" disabled={pupsSelectedForManagement.length === 0}>X</button>                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addBulkGenotypeLocus} className="secondary-button" style={{ width: '100%', marginTop: '10px' }} disabled={pupsSelectedForManagement.length === 0}>+ Add Locus</button>                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleApplyBulkPupChanges}
                    className="primary-button"
                    style={{ width: '100%', marginTop: '15px', padding: '12px' }}
                    disabled={pupsSelectedForManagement.length === 0}
                >
                    Apply Changes to Selected
                </button>

               
                {/* Tag by Formula */}
                <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Auto-Tag All Pups</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={tagFormula}
                            onChange={(e) => setTagFormula(e.target.value)}
                            placeholder="cage_dob_#"
                            style={{ 
                                flex: '1', 
                                padding: '8px', 
                                borderRadius: '4px', 
                                border: '1px solid #ddd',
                                fontSize: '0.9em'
                            }}
                        />
                        <button
                            type="button"
                            onClick={handleApplyTagFormula}
                            className="primary-button"
                            style={{ padding: '8px 12px', fontSize: '0.9em', whiteSpace: 'nowrap' }}
                            disabled={!tagFormula.trim()}
                        >
                            Apply
                        </button>
                    </div>
                    <div style={{ fontSize: '0.75em', color: '#666', marginTop: '5px' }}>
                        <code>cage</code>, <code>dob</code>, <code>litter</code>, <code>#</code> (pup number)
                    </div>
                </div>

            </div>
        );
    };


    const renderBulkAssignControls = () => (
        <div className="bulk-assign-controls" style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', margin: '15px 0', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <select value={bulkAssignCageId} onChange={(e) => setBulkAssignCageId(e.target.value)} style={{ flex: '1 1 400px', minWidth: '200px', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <option value="">Select a cage for bulk assignment...</option>
                    {cages.map(cage => {
                        const effectiveOccupancy = getEffectiveCageOccupancy(cage);
                        return <option key={cage.id} value={cage.id}>{cage.display_cage_number} - {cage.location} ({effectiveOccupancy}/{cage.capacity})</option>
                    })}
                </select>
                <div style={{display: 'flex', gap: '10px', flex: '1 1 auto'}}>
                    <button type="button" onClick={selectedGroups.length > 0 ? handleApplyBulkGroupAssignment : handleApplyBulkCageChange} className="primary-button" style={{ padding: '10px 15px', flex: '1' }} disabled={(pupsSelectedForWeaning.length === 0 && selectedGroups.length === 0) || !bulkAssignCageId}>
                        Apply ({selectedGroups.length > 0 ? `${selectedGroups.length} groups` : `${pupsSelectedForWeaning.length} pups`})
                    </button>
                    <button type="button" onClick={() => setShowNewCageFormWeaning(true)} className="secondary-button" style={{ padding: '10px 15px', flex: '1' }}>New Cage</button>
                </div>
            </div>
            {showNewCageFormWeaning && (
                <div style={{ border: '1px solid #007bff', padding: '10px', borderRadius: '4px', marginTop: '10px', background: '#e7f3ff' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '1em' }}>Create New Cage</h4>
                    <input type="text" placeholder="Cage number" value={newCageWeaning.display_cage_number} onChange={(e) => setNewCageWeaning({ ...newCageWeaning, display_cage_number: e.target.value })} style={{ marginBottom: '5px', width: '100%', padding: '8px', boxSizing: 'border-box' }} required />
                    <input type="text" placeholder="Location" value={newCageWeaning.location} onChange={(e) => setNewCageWeaning({ ...newCageWeaning, location: e.target.value })} style={{ marginBottom: '10px', width: '100%', padding: '8px', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button type="button" onClick={handleNewCageSubmitWeaning} className="primary-button" style={{ flex: 1, padding: '8px', fontSize: '0.9em' }}>Create</button>
                        <button type="button" onClick={() => setShowNewCageFormWeaning(false)} className="secondary-button" style={{ flex: 1, padding: '8px', fontSize: '0.9em' }}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderMobileWeanPups = () => (
        <div className="mobile-list" style={{ padding: '0 5px' }}>
            {autoGroupedPups.map(group => {
                const isExpanded = expandedGroups[group.id];
                const isSelected = selectedGroups.includes(group.id);
                // Check if ALL pups in the group are assigned to the SAME cage
                let groupCageDisplay = 'Not Assigned';
                const firstAssignedPupCageId = group.pups.length > 0 ? pupCageAssignments[group.pups[0].id] : undefined;
                if (firstAssignedPupCageId) {
                    const allSame = group.pups.every(p => pupCageAssignments[p.id] === firstAssignedPupCageId);
                    if (allSame) {
                        const cage = cages.find(c => c.id == firstAssignedPupCageId);
                        groupCageDisplay = cage ? cage.display_cage_number : 'Assigned';
                    } else {
                        groupCageDisplay = 'Mixed';
                    }
                }

                return (
                    <div key={group.id} className="list-item" style={{
                        border: '1px solid #e0e0e0',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        marginBottom: '12px',
                        padding: '0',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}>
                        {/* Group Header */}
                        <div onClick={() => handleGroupCheckboxChange(group.id)} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px',
                            backgroundColor: isSelected ? '#e7f3ff' : '#f8f9fa'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleGroupCheckboxChange(group.id)}
                                    style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{group.label}</div>
                                    <div style={{
                                        fontSize: '0.85em',
                                        fontWeight: 'bold',
                                        color: groupCageDisplay === 'Not Assigned' ? '#6c757d' : '#28a745'
                                    }}>
                                        {groupCageDisplay}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); toggleGroupExpansion(group.id); }}
                                className="tertiary-button"
                                style={{ fontSize: '1.5em', padding: '0 8px', minWidth: 'auto', backgroundColor: 'transparent', border: 'none', color: '#555' }}
                            >
                                {isExpanded ? '-' : '+'}
                            </button>
                        </div>

                        {/* Pup Details */}
                        {isExpanded && (
                            <div style={{ padding: '5px 12px 12px 12px' }}>
                                {group.pups.map((pup, index) => {
                                    const pupAssignedCage = cages.find(c => c.id == pupCageAssignments[pup.id]);
                                    return (
                                        <div key={pup.id} onClick={() => handleWeanPupCheckboxChange(pup.id)} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: pupsSelectedForWeaning.includes(pup.id) ? '#e7f3ff' : 'transparent',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            marginTop: index > 0 ? '5px' : '0'
                                        }}>
                                            <input type="checkbox" checked={pupsSelectedForWeaning.includes(pup.id)} onChange={() => handleWeanPupCheckboxChange(pup.id)} onClick={(e) => e.stopPropagation()} style={{ marginRight: '12px', transform: 'scale(1.1)' }} />
                                            <div style={{ flex: 1, lineHeight: '1.4' }}>
                                                <div style={{ fontWeight: '500' }}>
                                                    {pup.physical_tag || `Pup #${index + 1}`} ({pup.sex})
                                                </div>
                                                <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                    {formatGenotype(pup.genotype_data)}
                                                </div>
                                            </div>
                                            <div style={{
                                                fontWeight: 'bold',
                                                color: pupAssignedCage ? '#28a745' : 'transparent',
                                                fontSize: '0.9em'
                                            }}>
                                                {pupAssignedCage ? pupAssignedCage.display_cage_number : ''}
                                             </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    );

    return (
        <div className="litter-management">
            {confirmDialog && (
                <ConfirmDialog
                    message={confirmDialog.message}
                    confirmLabel={confirmDialog.confirmLabel}
                    danger={confirmDialog.danger}
                    onConfirm={() => { confirmDialog.resolve(true); setConfirmDialog(null); }}
                    onCancel={() => { confirmDialog.resolve(false); setConfirmDialog(null); }}
                />
            )}
            <div className="main-content litter-management-grid">
                <div className="mice-section">
                    <div className="sticky-toolbar">
                    <div className="search-bar">
                        <input type="text" placeholder="Search..." value={searchQueryLitters} onChange={handleSearchLitters} />
                        {searchQueryLitters && <button onClick={clearSearchLitters} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#666', width: 'auto' }} title="Clear search">✕</button>}
                    </div>
                    {!isMobile && (
                        <div className="action-buttons">
                            <button onClick={handleWeanSelectedPups} className="primary-button fab" style={{backgroundColor: '#28a745'}} disabled={selectedLittersForActions.length !== 1}>Wean ({selectedLittersForActions.length})</button>
                           {user.lab_id && labMembers.length > 0 && (
                               <button
                                   onClick={handleTransferSelectedLitters}
                                   className="primary-button fab"
                                   style={{ backgroundColor: '#17a2b8' }}
                                   disabled={selectedLittersForActions.length === 0}
                               >
                                   Transfer ({selectedLittersForActions.length})
                               </button>
                           )}
                            <button onClick={handleDeleteSelectedLitters} className="secondary-button fab" style={{ backgroundColor: '#dc3545' }} disabled={selectedLittersForActions.length === 0}>Archive ({selectedLittersForActions.length})</button>
                        </div>
                    )}
                    </div>
                    {loadError && <div className="error">{loadError}</div>}
                    {loading ? (
                        <SkeletonRow count={itemsPerPageLitters > 10 ? 10 : itemsPerPageLitters} />
                    ) : litters.length === 0 && totalLittersCount === 0 ? (
                        <div className="empty-state">
                            <p>No active litters found.</p>
                            <p>Create a litter from a breeding cage to start tracking pups.</p>
                        </div>
                    ) : (
                        <>
                            <div className="litter-list-container">{isMobile ? renderMobileLitterList() : renderDesktopLitterTable()}</div>
                            {!isMobile && totalLittersCount > 0 && (<div className="pagination-controls"><div>Items per page:<select onChange={handleItemsPerPageChangeLitters} value={itemsPerPageLitters}>{ITEMS_PER_PAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div><div><button onClick={() => handlePageChangeLitters(currentPageLitters - 1)} disabled={currentPageLitters === 1}>Previous</button><span>Page {currentPageLitters} of {totalPagesLitters}</span><button onClick={() => handlePageChangeLitters(currentPageLitters + 1)} disabled={currentPageLitters === totalPagesLitters}>Next</button></div></div>)}
                            {isMobile && isLoadingMore && (<div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Loading more...</div>)}
                        </>
                    )}
                </div>
            </div>

            {showPupManagementModal && managingLitter && (
                <LitterForm
                    managingLitter={managingLitter}
                    isMobile={isMobile}
                    overlayRef={modalOverlayRef}
                    onLitterDetailChange={handleLitterDetailChange}
                    onAddPup={handleAddPup}
                    renderPupTable={renderPupManagementTable}
                    renderMobilePupList={renderMobilePupManagement}
                    renderMobileAutoTagControls={renderMobileAutoTagControls}
                    renderMobileBulkControls={renderMobileBulkManageControls}
                    renderBulkControls={renderBulkManageControls}
                    pupsSelectedForManagement={pupsSelectedForManagement}
                    isBulkEditExpanded={isBulkEditExpanded}
                    onSave={handleSavePupRecords}
                    isSubmitting={isSubmitting}
                    onCancel={() => setShowPupManagementModal(false)}
                    error={formError}
                />
            )}

            {showWeanPupsModal && weaningLitter && (
                <WeaningConfirm
                    weaningLitter={weaningLitter}
                    isMobile={isMobile}
                    overlayRef={modalOverlayRef}
                    autoGroupedPups={autoGroupedPups}
                    expandedGroups={expandedGroups}
                    selectedGroups={selectedGroups}
                    pupsSelectedForWeaning={pupsSelectedForWeaning}
                    pupCageAssignments={pupCageAssignments}
                    cages={cages}
                    onToggleGroup={handleGroupCheckboxChange}
                    onToggleGroupExpansion={toggleGroupExpansion}
                    onTogglePup={handleWeanPupCheckboxChange}
                    formatGenotype={formatGenotype}
                    renderMobileList={renderMobileWeanPups}
                    renderBulkAssignControls={renderBulkAssignControls}
                    onConfirm={handleConfirmWeanPups}
                    isSubmitting={isSubmitting}
                    onCancel={() => setShowWeanPupsModal(false)}
                    error={formError}
                />
            )}


           {/* Transfer Modal */}
           {showTransferModal && (
               <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                   <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
                       <h3>Transfer {selectedLittersForActions.length} Litter(s)</h3>
                       <p>Transfer the selected litter(s) to another lab member. This will archive the litters for you and create new copies for the recipient. Note: Parent mouse associations will be removed since parents belong to you.</p>
                       
                       <div className="form-group">
                           <label>Transfer to:</label>
                           <select 
                               value={transferRecipientId} 
                               onChange={(e) => setTransferRecipientId(e.target.value)}
                               style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                           >
                               <option value="">Select lab member...</option>
                               {labMembers.map(member => (
                                   <option key={member.id} value={member.id}>
                                       {member.first_name && member.last_name 
                                           ? `${member.first_name} ${member.last_name} (${member.username})`
                                           : member.username}
                                   </option>
                               ))}
                           </select>
                       </div>
                       
                       {formError && <div className="error">{formError}</div>}
                       
                       <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                           <button onClick={handleConfirmLitterTransfer} style={{ flex: 1 }} className="primary-button" disabled={isTransferringLitters}>{isTransferringLitters ? 'Transferring...' : 'Confirm Transfer'}</button>
                           <button onClick={() => setShowTransferModal(false)} className="secondary-button" style={{ flex: 1 }}>Cancel</button>
                       </div>
                   </div>
               </div>
           )}
        </div>
    );
};

export default LitterManagement;